import React from 'react';
import { moduleCategories } from '../config/moduleCategories';
import { text } from '../constants/configurationText';

const CATEGORY_COLORS = {
  'Modules':                { bg: '#2e7cba', light: 'rgba(46,124,186,0.1)',  border: 'rgba(46,124,186,0.25)' },
  'Platform Settings':      { bg: '#6366f1', light: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.25)' },
  'Core HR Modules':        { bg: '#2e7cba', light: 'rgba(46,124,186,0.1)',  border: 'rgba(46,124,186,0.25)' },
  'Payroll Modules':        { bg: '#10b981', light: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.25)' },
  'Document Modules':       { bg: '#f59e0b', light: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)' },
  'Communication Modules':  { bg: '#8b5cf6', light: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.25)' },
  'Calendar & Events':      { bg: '#0ea5e9', light: 'rgba(14,165,233,0.1)', border: 'rgba(14,165,233,0.25)' },
  'Employee Actions':       { bg: '#f97316', light: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.25)' },
  'Reports & Analytics':    { bg: '#14b8a6', light: 'rgba(20,184,166,0.1)', border: 'rgba(20,184,166,0.25)' },
  'Advanced Features':      { bg: '#6366f1', light: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.25)' },
  'Admin Features':         { bg: '#ef4444', light: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.25)' },
};

const ModulePermissions = ({ permissions, styles, onToggle, theme, t = text.en, onlyCategories = null }) => {
  const entries = Object.entries(moduleCategories).filter(
    ([category]) => !onlyCategories || onlyCategories.includes(category),
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {entries.map(([category, modules]) => {
        const color = CATEGORY_COLORS[category] || CATEGORY_COLORS['Modules'];
        const enabledCount = modules.filter(m => permissions[m.key]).length;

        return (
          <div key={category} style={{
            borderRadius: '14px',
            border: `1px solid ${color.border}`,
            overflow: 'hidden',
            background: theme === 'light' ? '#ffffff' : 'rgba(255,255,255,0.02)',
          }}>
            {/* Category Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: color.light,
              borderBottom: theme === 'light' ? `1px solid ${color.border}` : `1px solid ${color.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: color.bg, flexShrink: 0,
                }} />
                <span style={{
                  fontSize: '12.5px',
                  fontWeight: '800',
                  color: color.bg,
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                }}>
                  {t.categoryNames[category] || category}
                </span>
              </div>
              <span style={{
                fontSize: '12px',
                fontWeight: '700',
                color: color.bg,
                background: theme === 'light' ? '#ffffff' : 'rgba(0,0,0,0.2)',
                padding: '3px 10px',
                borderRadius: '999px',
                border: `1px solid ${color.border}`,
              }}>
                {enabledCount}/{modules.length}
              </span>
            </div>

            {/* Modules Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
              gap: '1px',
              background: theme === 'light' ? '#eef2f7' : 'rgba(255,255,255,0.06)',
            }}>
              {modules.map((module, idx) => {
                const isOn = !!permissions[module.key];
                return (
                  <div
                    key={module.key}
                    onClick={() => onToggle(module.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: theme === 'light' ? '#ffffff' : '#0f1a2b',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      userSelect: 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme === 'light'
                        ? color.light : 'rgba(255,255,255,0.04)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = theme === 'light'
                        ? '#ffffff' : '#0f1a2b';
                    }}
                  >
                    <span style={{
                      fontSize: '13px',
                      fontWeight: isOn ? '600' : '500',
                      color: isOn
                        ? (theme === 'light' ? '#0f172a' : '#ffffff')
                        : (theme === 'light' ? '#94a3b8' : 'rgba(255,255,255,0.35)'),
                      lineHeight: 1.3,
                      transition: 'color 0.15s',
                    }}>
                      {t.modules[module.key] || module.label}
                    </span>

                    {/* Mini Toggle */}
                    <div style={{
                      width: '32px',
                      height: '18px',
                      borderRadius: '9px',
                      background: isOn ? color.bg : (theme === 'light' ? '#e2e8f0' : 'rgba(255,255,255,0.12)'),
                      position: 'relative',
                      flexShrink: 0,
                      marginInlineStart: '8px',
                      transition: 'background 0.2s',
                      boxShadow: isOn ? `0 0 0 2px ${color.light}` : 'none',
                    }}>
                      <div style={{
                        width: '13px',
                        height: '13px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        position: 'absolute',
                        top: '2.5px',
                        left: isOn ? '16.5px' : '2.5px',
                        transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ModulePermissions;